// Preview bridge — postMessage channel between the admin editor's docked
// iframe preview (v2-editor/editor/form-mode.ts, Fase C4) and the REAL
// customer embed running inside it.
//
// Purely additive + opt-in: only activates when the host page is framed
// (window.parent !== window) AND carries `?bridge=1` (form-mode.ts's own
// query param on its /preview.html iframe src) — a real storefront mount
// never sees either condition, so this module is a no-op there. Mirrors
// the existing postMessage precedent in personalizer-settings.ts: a
// narrow `type` discriminator, no origin check (established convention —
// the admin only ever points this iframe at its own same-origin
// /preview.html, so there's no cross-origin payload to guard against).
//
// Scope (see NOTES.md N73): this implements ONLY the "field focus" half
// of the plan's C4 item — clicking/focusing a field in this live preview
// tells the editor which layer to expand + scroll to in the form-mode
// left column (DFP_FIELD_FOCUSED), and the editor can ask this preview
// to scroll-to + flash a field in return (DFP_HIGHLIGHT_FIELD), hopping
// wizard steps via the wizard's OWN tab click listener
// (wizard/tabs-cart.ts's wireDynamicTabs) so this never touches
// runtime.ts's step-navigation internals directly. The OTHER half of
// that plan item — replacing the draft-POST + iframe-reload doc delivery
// with an in-place hot-swap — is explicitly NOT implemented here; the
// docked preview still refreshes via form-mode.ts's existing debounced
// saveDraftVersion + src reassignment.

const HIGHLIGHT_CLASS = 'dfp-bridge-highlight';
const HIGHLIGHT_MS = 1600;
const STYLE_TAG_ID = 'dfp-bridge-style';

let started = false;

function isBridgedFrame(): boolean {
  if (window.parent === window) return false;
  try {
    return new URLSearchParams(window.location.search).get('bridge') === '1';
  } catch {
    return false;
  }
}

function injectHighlightStyle(): void {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const tag = document.createElement('style');
  tag.id = STYLE_TAG_ID;
  tag.textContent = `
    .${HIGHLIGHT_CLASS} {
      outline: 2px solid var(--dfp-accent, #ff6a4d) !important;
      outline-offset: 3px !important;
      border-radius: var(--dfp-r-md, 8px);
      animation: dfpBridgePulse 1.6s ease-out;
    }
    @keyframes dfpBridgePulse {
      0%   { outline-color: var(--dfp-accent, #ff6a4d); box-shadow: 0 0 0 6px rgba(255, 106, 77, 0.18); }
      70%  { outline-color: var(--dfp-accent, #ff6a4d); box-shadow: 0 0 0 6px rgba(255, 106, 77, 0); }
      100% { outline-color: transparent; box-shadow: 0 0 0 0 rgba(255, 106, 77, 0); }
    }
  `;
  document.head.appendChild(tag);
}

/** Outbound: report which `[data-layer-id]` field the customer form just
 *  focused/clicked. Deduped on the id so retyping inside the same field
 *  doesn't spam postMessage on every keystroke — only actual field
 *  changes are reported. Capture-phase so the wizard's own
 *  stopImmediatePropagation() calls (CTA / back button) can't swallow it. */
function wireOutbound(): void {
  let lastFocusedId: string | null = null;
  const report = (target: EventTarget | null): void => {
    const el = target instanceof Element ? target.closest<HTMLElement>('[data-layer-id]') : null;
    const layerId = el?.dataset.layerId;
    if (!layerId || layerId === lastFocusedId) return;
    lastFocusedId = layerId;
    window.parent.postMessage({ type: 'DFP_FIELD_FOCUSED', layerId }, '*');
  };
  document.addEventListener('focusin', (ev) => report(ev.target), true);
  document.addEventListener('click', (ev) => report(ev.target), true);
}

/** Inbound: scroll to + flash a field the editor's form-mode column just
 *  expanded. If the field lives on a non-active wizard step, click its
 *  tab first — reusing wireDynamicTabs' own listener (wizard/tabs-cart.ts)
 *  so the step switch behaves exactly like a real customer click instead
 *  of reaching into runtime.ts's step-navigation state directly. */
function wireInbound(): void {
  window.addEventListener('message', (ev) => {
    const data = ev.data as { type?: string; layerId?: string } | null;
    if (!data || data.type !== 'DFP_HIGHLIGHT_FIELD' || !data.layerId) return;
    const el = document.querySelector<HTMLElement>(`[data-layer-id="${CSS.escape(data.layerId)}"]`);
    if (!el) return;

    const pane = el.closest<HTMLElement>('.bd-pane[data-bd-pane]');
    if (pane && !pane.classList.contains('is-active')) {
      document.querySelector<HTMLButtonElement>(`.bd-tab[data-bd-tab="${pane.dataset.bdPane}"]`)?.click();
    }

    // A macrotask (not requestAnimationFrame) before measuring: the tab
    // click's DOM mutations (`.is-active` / `hidden`) already landed
    // synchronously, so this isn't waiting for a paint — it's just
    // yielding one tick. rAF was tried first but browsers suspend it
    // entirely for non-visible documents (backgrounded tab, hidden
    // iframe), which would silently strand this in that case; a plain
    // timeout still fires there.
    setTimeout(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove(HIGHLIGHT_CLASS);
      // Re-trigger the CSS animation on repeat highlights of the same field.
      void el.offsetWidth;
      el.classList.add(HIGHLIGHT_CLASS);
      setTimeout(() => el.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS);
    }, 0);
  });
}

/** Boots the bridge once. No-op outside a `?bridge=1` framed preview —
 *  safe to call unconditionally at module load on every mount path. */
export function startPreviewBridge(): void {
  if (started || !isBridgedFrame()) return;
  started = true;
  injectHighlightStyle();
  wireOutbound();
  wireInbound();
}
